import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Counter from "@/components/Counter";
import CTA from "@/components/CTA";
import { PILLARS, SERVICES, PROJECTS, MARQUEE_ITEMS } from "@/data/content";

const METRICS = [
  { value: 1.9, prefix: "£", suffix: "B+", decimals: 1, label: "Combined portfolio GDV" },
  { value: 22, suffix: "+", label: "Years chartered experience" },
  { value: 8, suffix: "", label: "Landmark UK schemes" },
  { value: 6500, suffix: "", label: "Homes in our largest new town" },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute -inset-y-16 inset-x-0 overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover grayscale opacity-45 scale-110"
          data-testid="hero-background-video"
        >
          <source src="/assets/hero.webm" type="video/webm" />
          <source src="/assets/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-void/80 via-void/40 to-void" />
        <div className="absolute inset-0 bg-gradient-to-r from-void/80 via-transparent to-void/60" />
      </motion.div>
      <div className="absolute inset-0 blueprint-grid opacity-70 pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative max-w-7xl mx-auto px-6 w-full pt-48 pb-14">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-400 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" />
            Fox Project Management · Construction PM Consultancy — UK &amp; Ireland
          </p>
        </Reveal>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-display font-extrabold uppercase tracking-tight leading-[0.92] text-[9vw] sm:text-[10vw] lg:text-[7rem]"
        >
          <MaskedLine delay={0.15}>
            <span className="text-zinc-50">Smart.</span>
          </MaskedLine>
          <MaskedLine delay={0.32}>
            <span className="text-outline">Agile.</span>
          </MaskedLine>
          <MaskedLine delay={0.49}>
            <span className="text-zinc-50">Adaptive.</span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <Reveal delay={0.5} className="max-w-xl">
            <p className="text-base text-zinc-300 leading-relaxed font-light">
              Straightforward communication. Uncomplicated processes. We help clients deliver construction and
              development projects with confidence and certainty — from inception to handover.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                data-testid="hero-cta-button"
                to="/contact"
                className="group inline-flex items-center gap-3 bg-zinc-50 text-zinc-950 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] transition-colors duration-300"
              >
                Start a project
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                data-testid="hero-portfolio-button"
                to="/portfolio"
                className="inline-flex items-center gap-3 border border-zinc-700 text-zinc-200 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:border-zinc-400 hover:text-zinc-50 transition-colors duration-300"
              >
                View portfolio <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="flex gap-10 lg:gap-12 font-mono border-l border-zinc-700/80 pl-8">
              {[
                { v: "£300M", l: "Largest development" },
                { v: "22+", l: "Years" },
                { v: "8", l: "Schemes" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="text-xl sm:text-2xl text-zinc-50 font-medium">{s.v}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </motion.div>
    </section>
  );
}

function Philosophy() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32" data-testid="philosophy-section">
      <Reveal>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
          <span className="w-10 h-px bg-[#C5A059]" /> Our philosophy
        </p>
        <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 max-w-2xl leading-tight">
          Built to be different from the rest
        </h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-px bg-zinc-800/60 border border-zinc-800/60">
        {PILLARS.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.12}>
            <div
              data-testid={`philosophy-card-${p.id}`}
              className="group relative h-full bg-void p-8 sm:p-10 hover:bg-void-2 transition-colors duration-500"
            >
              <span className="font-display text-5xl font-extrabold text-zinc-800 group-hover:text-[#C5A059] transition-colors duration-500">
                {p.num}
              </span>
              <h3 className="mt-6 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-zinc-100">
                {p.title}
              </h3>
              <p className="mt-4 text-sm text-zinc-400 leading-relaxed font-light">{p.text}</p>
              <p className="mt-6 pt-6 border-t border-zinc-800/80 text-xs text-zinc-500 leading-relaxed">
                <span className="font-mono uppercase tracking-[0.2em] text-[#C5A059] block mb-2">
                  What it means for you
                </span>
                {p.benefit}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Metrics() {
  return (
    <section className="border-y border-zinc-800/60 bg-void-2/40">
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {METRICS.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.1}>
            <div data-testid={`metric-card-${i}`} className="border-l border-zinc-800 pl-6">
              <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-zinc-50 tracking-tight">
                <Counter end={m.value} prefix={m.prefix || ""} suffix={m.suffix} decimals={m.decimals || 0} />
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">{m.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32">
      <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" /> What we do
          </p>
          <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
            Three disciplines. One standard.
          </h2>
        </div>
        <Link
          to="/services"
          data-testid="services-view-all-link"
          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-50 transition-colors"
        >
          All services <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.12}>
            <Link
              to="/services"
              data-testid={`service-card-${s.id}`}
              className="group block h-full border border-zinc-800/80 bg-void-2/40 p-8 hover:border-zinc-500 hover:bg-void-2 transition-all duration-500"
            >
              <span className="font-mono text-xs text-[#C5A059] tracking-[0.2em]">{s.num}</span>
              <h3 className="mt-5 font-display text-lg sm:text-xl font-bold text-zinc-100 group-hover:text-white transition-colors">
                {s.title}
              </h3>
              <p className="mt-4 text-sm text-zinc-400 font-light leading-relaxed">{s.short}</p>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 group-hover:text-zinc-200 transition-colors">
                Explore <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FeaturedProjects() {
  const [masterplan] = PROJECTS;
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24 sm:pb-32">
      <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" /> Featured work
          </p>
          <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
            Fountainbridge — a landmark in development
          </h2>
        </div>
        <Link
          to="/portfolio"
          data-testid="portfolio-view-all-link"
          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-50 transition-colors"
        >
          Full portfolio <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </Reveal>

      <div className="mt-14">
        <Reveal>
          <Link
            to={`/portfolio/${masterplan.id}`}
            data-testid="featured-project-fountainbridge"
            className="group relative block overflow-hidden border border-zinc-800/80 h-[420px] lg:h-[560px]"
          >
            <img
              src={masterplan.image}
              alt={masterplan.title}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ease-out group-hover:scale-[1.04]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{masterplan.category}</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-zinc-50">{masterplan.title}</h3>
              <p className="mt-3 max-w-xl text-sm text-zinc-300 font-light">{masterplan.summary}</p>
              <p className="mt-4 font-mono text-xs text-zinc-400 tracking-wider">
                {masterplan.location} · {masterplan.value} · {masterplan.client}
              </p>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

import ClientLogos from "@/components/ClientLogos";

function ClientStrip() {
  return (
    <section className="border-t border-zinc-800/60">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 text-center">
            Experience earned alongside leading organisations throughout a 22-year career
          </p>
        </Reveal>
        <ClientLogos />
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee items={MARQUEE_ITEMS} />
      <Philosophy />
      <Metrics />
      <ServicesPreview />
      <FeaturedProjects />
      <ClientStrip />
      <CTA text="Director-led advice on every instruction. Tell us where your project is heading and we'll help you get it there with certainty." />
    </>
  );
}
